import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Backspace as BackspaceIcon,
  Call as CallIcon,
  PhoneDisabled as PhoneDisabledIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  EventAvailable as EventAvailableIcon,
  Dialpad as DialpadIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { businessConfigService } from '../services/businessConfigService';
import { apiService } from '../services/apiService';

const DemoCallPage = () => {
  const { customerId } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [meetingType, setMeetingType] = useState('');
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [demoConfig, setDemoConfig] = useState(null);
  const [calling, setCalling] = useState(false);
  const [callId, setCallId] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [error, setError] = useState(null);
  const [agreedMeetingDate, setAgreedMeetingDate] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const statusCheckInterval = useRef(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Fetch business config to check for isp_demo_config
        const businessData = await businessConfigService.getBusinessConfig(customerId);
        
        if (!businessData.isp_demo_config) {
          setError(t('demoCall.errorNotConfigured'));
          setLoading(false);
          return;
        }

        setDemoConfig(businessData.isp_demo_config);

        // Fetch calendars to get meeting types
        const calendars = await businessConfigService.getBusinessCalendars(customerId);
        
        // Extract meeting types from the first calendar (or merge from all calendars)
        if (calendars && calendars.length > 0) {
          const allMeetingTypes = {};
          
          // Merge meeting types from all calendars
          calendars.forEach(calendar => {
            if (calendar.meeting_types) {
              Object.assign(allMeetingTypes, calendar.meeting_types);
            }
          });
          
          if (Object.keys(allMeetingTypes).length > 0) {
            const types = Object.entries(allMeetingTypes).map(([key, value]) => ({
              id: key,
              description: value.description || key,
            }));
            setMeetingTypes(types);
            if (types.length > 0) {
              setMeetingType(types[0].id);
            }
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching configuration:', err);
        setError(t('demoCall.errorLoadConfig'));
        setLoading(false);
      }
    };

    fetchConfig();

    // Cleanup interval on unmount
    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }
    };
  }, [customerId]);

  const handleKeypadClick = (digit) => {
    if (phoneNumber.length < 15) {
      setPhoneNumber(phoneNumber + digit);
    }
  };

  const handleBackspace = () => {
    setPhoneNumber(phoneNumber.slice(0, -1));
  };

  const handleClear = () => {
    setPhoneNumber('');
  };

  const formatPhoneNumber = (number) => {
    // Remove non-digits
    const digits = number.replace(/\D/g, '');
    
    // Add +48 prefix if not present
    if (digits.startsWith('0048')) {
      return `+${digits.substring(2)}`;
    } else if (digits.startsWith('48')) {
      return `+${digits}`;
    } else if (digits.length === 9) {
      return `+48${digits}`;
    }
    return `+${digits}`;
  };

  const checkCallStatus = async (id) => {
    try {
      const status = await apiService.getDemoCallStatus(customerId, id);
      
      if (status.success) {
        setCallStatus(status.data);
        
        // Debug logging
        console.log('Full status response:', status);
        console.log('status.data:', status.data);
        console.log('dynamic_variables:', status.data.dynamic_variables);
        
        // Get current call state
        const callState = status.data.status || status.data.call_status || '';
        
        // All possible terminal states according to ElevenLabs API:
        // - done: call completed successfully
        // - failed: call failed
        // - ended: call ended (user hung up)
        // - completed: alternative completion status
        // - finished: alternative completion status
        const terminalStates = ['done', 'failed', 'ended', 'completed', 'finished'];
        const isFinished = terminalStates.includes(callState.toLowerCase());
        
        console.log(`Call status: ${callState}, isFinished: ${isFinished}`);
        
        if (isFinished) {
          // Stop polling
          if (statusCheckInterval.current) {
            clearInterval(statusCheckInterval.current);
            statusCheckInterval.current = null;
          }
          
          // Extract agreed_meeting_date from dynamic_variables
          // The backend already extracts this from tool_results and places it here
          const dynamicVars = status.data.dynamic_variables || {};
          const agreedDate = dynamicVars.agreed_meeting_date;
          
          console.log('Call finished. Dynamic variables:', dynamicVars);
          console.log('Agreed meeting date:', agreedDate);
          
          if (agreedDate) {
            setAgreedMeetingDate(agreedDate);
          } else {
            console.warn('No agreed_meeting_date found - booking may not have been completed');
          }
          
          setCalling(false);
        }
      }
    } catch (err) {
      console.error('Error checking call status:', err);
    }
  };

  const handleInitiateCall = async () => {
    if (!phoneNumber || !meetingType) {
      setError(t('demoCall.errorEnterDetails'));
      return;
    }

    setError(null);
    setCalling(true);
    setCallStatus(null);
    setAgreedMeetingDate(null);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      const result = await apiService.initiateDemoCall(customerId, {
        phone: formattedPhone,
        meeting_type: meetingType,
      });

      if (result.success && result.call_id) {
        setCallId(result.call_id);
        
        // Start polling call status every 5 seconds
        statusCheckInterval.current = setInterval(() => {
          checkCallStatus(result.call_id);
        }, 5000);
        
        // Check immediately
        checkCallStatus(result.call_id);
      } else {
        setError(result.error || t('demoCall.errorCallFailed'));
        setCalling(false);
      }
    } catch (err) {
      console.error('Error initiating call:', err);
      setError(t('demoCall.errorCallFailed'));
      setCalling(false);
    }
  };

  const handleStopCall = async () => {
    if (!callId) return;

    try {
      const result = await apiService.stopDemoCall(customerId, callId);
      
      if (result.success) {
        // Stop polling
        if (statusCheckInterval.current) {
          clearInterval(statusCheckInterval.current);
          statusCheckInterval.current = null;
        }
        
        // Update UI
        setCalling(false);
        setCallStatus({ status: 'stopped' });
      } else {
        setError(result.error || t('demoCall.errorStopCallFailed'));
      }
    } catch (err) {
      console.error('Error stopping call:', err);
      setError(t('demoCall.errorStopCallFailed'));
    }
  };

  const getMeetingTypeDescription = () => {
    const type = meetingTypes.find(t => t.id === meetingType);
    return type ? type.description : meetingType;
  };

  const getStatusMessage = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'initiated':
        return t('demoCall.statusInitiated');
      case 'in-progress':
        return t('demoCall.statusInProgress');
      case 'processing':
        return t('demoCall.statusProcessing');
      case 'done':
        return t('demoCall.statusDone');
      case 'failed':
        return t('demoCall.statusFailed');
      case 'ended':
        return t('demoCall.statusEnded');
      case 'stopped':
        return t('demoCall.statusStopped');
      default:
        return status;
    }
  };

  const renderCallResult = () => {
    const callState = callStatus?.status || '';
    const callStateLower = callState.toLowerCase();
    
    // If call failed
    if (callStateLower === 'failed') {
      return (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mt: 3 }}>
          <Typography variant="body1">
            {t('demoCall.meetingFailed')}
          </Typography>
        </Alert>
      );
    }
    
    // If call ended by user (hung up)
    if (callStateLower === 'ended' && !agreedMeetingDate) {
      return (
        <Alert severity="warning" icon={<ErrorIcon />} sx={{ mt: 3 }}>
          <Typography variant="body1">
            {t('demoCall.callEndedByUser')}
          </Typography>
        </Alert>
      );
    }
    
    // If call completed but no booking
    if ((callStateLower === 'done' || callStateLower === 'completed') && !agreedMeetingDate) {
      return (
        <Alert severity="warning" icon={<ErrorIcon />} sx={{ mt: 3 }}>
          <Typography variant="body1">
            {t('demoCall.callCompletedNoBooking')}
          </Typography>
        </Alert>
      );
    }

    // If we have a meeting date - success!
    if (agreedMeetingDate) {
      return (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 3 }}>
          <Typography variant="body1">
            {t('demoCall.meetingFor')} <strong>'{getMeetingTypeDescription()}'</strong> {t('demoCall.scheduledFor')}{' '}
            <strong>{agreedMeetingDate}</strong>
          </Typography>
        </Alert>
      );
    }

    return null;
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (error && !demoConfig) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const keypadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
  ];

  const steps = [
    {
      label: t('demoCall.step1Title'),
      description: t('demoCall.step1Description'),
      icon: <EventAvailableIcon />,
    },
    {
      label: t('demoCall.step2Title'),
      description: t('demoCall.step2Description'),
      icon: <DialpadIcon />,
    },
    {
      label: t('demoCall.step3Title'),
      description: t('demoCall.step3Description'),
      icon: <CallIcon />,
    },
    {
      label: t('demoCall.step4Title'),
      description: t('demoCall.step4Description'),
      icon: <ScheduleIcon />,
    },
  ];

  const capabilities = [
    { icon: <PhoneIcon />, text: t('demoCall.capability1') },
    { icon: <ScheduleIcon />, text: t('demoCall.capability2') },
    { icon: <EventAvailableIcon />, text: t('demoCall.capability3') },
    { icon: <CheckCircleIcon />, text: t('demoCall.capability4') },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <PhoneIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
            >
              {t('demoCall.title')}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
            >
              {t('demoCall.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Typography 
          variant="body1" 
          paragraph
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {t('demoCall.description')}
        </Typography>
      </Paper>

      {/* How to Use - Interactive Instructions */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            mb: 3, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <VisibilityIcon color="primary" />
          {t('demoCall.howToUse')}
        </Typography>
        <Stepper orientation="vertical">
          {steps.map((step, index) => (
            <Step key={index} expanded active>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="h6">{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Capabilities */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          {t('demoCall.capabilities')}
        </Typography>
        <List>
          {capabilities.map((capability, index) => (
            <ListItem key={index}>
              <ListItemIcon sx={{ color: 'primary.main' }}>
                {capability.icon}
              </ListItemIcon>
              <ListItemText 
                primary={capability.text}
                primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Demo Call Interface */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          mb: 3,
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CallIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
            <Typography variant="h5" component="h2">
              {t('demoCall.initiateCall')}
            </Typography>
          </Box>
          
          {/* Call Status in Header */}
          {(calling || callStatus) && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Chip 
                label={getStatusMessage(callStatus?.status || 'initiated')}
                color={callStatus?.status === 'done' ? 'success' : callStatus?.status === 'failed' ? 'error' : 'info'}
                size="medium"
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Meeting Type Selector */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('demoCall.meetingType')}</InputLabel>
          <Select
            value={meetingType}
            onChange={(e) => setMeetingType(e.target.value)}
            label={t('demoCall.meetingType')}
            disabled={calling}
          >
            {meetingTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Phone Number Display */}
        <TextField
          fullWidth
          label={t('demoCall.phoneNumber')}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d+]/g, ''))}
          placeholder={t('demoCall.phonePlaceholder')}
          sx={{ mb: 3 }}
          disabled={calling}
          InputProps={{
            style: { fontSize: '1.5rem', textAlign: 'center' },
          }}
        />

        {/* Keypad */}
        <Box sx={{ mb: 3 }}>
          {keypadButtons.map((row, rowIndex) => (
            <Grid container spacing={1} key={rowIndex} sx={{ mb: 1 }}>
              {row.map((digit) => (
                <Grid item xs={4} key={digit}>
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ 
                      height: 60, 
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                    onClick={() => handleKeypadClick(digit)}
                    disabled={calling}
                  >
                    {digit}
                  </Button>
                </Grid>
              ))}
            </Grid>
          ))}
          
          {/* Action Buttons */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: 60 }}
                onClick={handleBackspace}
                disabled={calling || phoneNumber.length === 0}
                startIcon={<BackspaceIcon />}
              >
                {t('demoCall.keypadDelete')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: 60 }}
                onClick={handleClear}
                disabled={calling || phoneNumber.length === 0}
              >
                {t('demoCall.keypadClear')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Call Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleInitiateCall}
          disabled={calling || !phoneNumber || !meetingType}
          startIcon={calling ? <CircularProgress size={20} color="inherit" /> : <CallIcon />}
          sx={{ 
            height: 60, 
            fontSize: '1.2rem',
            mb: 2,
          }}
        >
          {calling ? t('demoCall.calling') : t('demoCall.initiateCall')}
        </Button>

        {/* Stop Call Button */}
        {calling && callId && (
          <Button
            variant="contained"
            color="error"
            size="large"
            fullWidth
            onClick={handleStopCall}
            startIcon={<PhoneDisabledIcon />}
            sx={{ 
              height: 60, 
              fontSize: '1.2rem',
              mb: 3,
            }}
          >
            {t('demoCall.stopCall')}
          </Button>
        )}



        {/* Call Result */}
        {!calling && callStatus && renderCallResult()}

        {/* Call ID for debugging */}
        {callId && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            {t('demoCall.callId')}: {callId}
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default DemoCallPage;
